<template>
  <div id="ui-translations">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="option-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="option-icon mr-2" :color="iconColor">
                mdi-translate
              </v-icon>
              {{ $t('uiTranslations.title') }}
            </h1>
          </div>
          <v-flex class="d-flex align-center mb-7 mt-12">
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold"
              @click="openCreate()"
            >
              {{ $t('uiTranslations.add') }}
            </v-btn>
          </v-flex>
        </div>

        <v-data-table
          :headers="headers"
          :items="translations"
          item-key="id"
          :items-per-page="50"
          :loading="loading"
        >
          <!-- Language -->
          <template #item.language="{ item }">
            <div class="tj-cell tj-w-110">
              <v-chip
                :color="item.language === 'en' ? 'primary' : 'secondary'"
                size="small"
              >
                {{ item.language.toUpperCase() }}
              </v-chip>
            </div>
          </template>

          <!-- Tour Steps Count -->
          <template #item.tourSteps="{ item }">
            <div class="tj-cell tj-w-110">
              <v-chip
                :color="item.tourSteps > 0 ? 'success' : 'error'"
                size="small"
              >
                {{ item.tourSteps }} steps
              </v-chip>
            </div>
          </template>

          <!-- Last Updated -->
          <template #item.updatedAt="{ item }">
            <div class="tj-cell tj-w-150">
              {{ formatDate(item.updatedAt) }}
            </div>
          </template>

          <!-- Row actions -->
          <template #item.actions="{ item }">
            <v-icon @click="openEdit(item)" class="mr-6">edit</v-icon>
            <v-icon v-if="!isHelpDesk" @click="remove(item)">delete_icon</v-icon>
          </template>
        </v-data-table>

        <!-- Edit Dialog -->
        <v-dialog v-model="dialog.open" max-width="800" scrollable>
          <v-card>
            <v-card-title class="text-h6">
              {{
                dialog.mode === 'create'
                  ? $t('uiTranslations.add')
                  : $t('common.edit')
              }}
            </v-card-title>
            <v-card-text>
              <v-form ref="formRef" v-model="formValid">
                <v-text-field
                  v-model="form.language"
                  :label="$t('uiTranslations.languageLabel')"
                  :disabled="dialog.mode === 'edit'"
                  :rules="[rules.required]"
                  autocapitalize="off"
                  autocomplete="off"
                  spellcheck="false"
                  @update:model-value="
                    (val) => (form.language = (val || '').trim().toLowerCase())
                  "
                />

                <!-- Tour Steps Editor -->
                <div class="mt-6">
                  <h3 class="text-h6 mb-4">
                    {{ $t('uiTranslations.tourSteps') }}
                  </h3>
                  <v-textarea
                    v-model="tourStepsJson"
                    :label="$t('uiTranslations.tourStepsJson')"
                    :hint="$t('uiTranslations.tourStepsHint')"
                    rows="10"
                    variant="outlined"
                    :error-messages="jsonError"
                    @update:model-value="validateJson"
                  />
                </div>

                <!-- Other UI Translations -->
                <div class="mt-6">
                  <h3 class="text-h6 mb-4">
                    {{ $t('uiTranslations.otherTranslations') }}
                  </h3>
                  <v-textarea
                    v-model="otherTranslationsJson"
                    :label="$t('uiTranslations.otherTranslationsJson')"
                    :hint="$t('uiTranslations.otherTranslationsHint')"
                    rows="8"
                    variant="outlined"
                    :error-messages="otherJsonError"
                    @update:model-value="validateOtherJson"
                  />
                </div>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="dialog.open = false">
                {{ $t('common.cancel') }}
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!formValid || !!jsonError || !!otherJsonError"
                @click="persist()"
              >
                {{ $t('common.save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from 'vue'
  import { useTheme } from 'vuetify'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { useI18n } from 'vue-i18n'
  import db from '@/firebase/init'
  import { useStore } from 'vuex'

  export default {
    name: 'UITranslations',
    components: {
      HowToUseThisPage,
    },
    setup() {
      const { t, locale } = useI18n({ useScope: 'global' })
      const store = useStore()

      const translations = ref([])
      const loading = ref(false)
      const dialog = ref({ open: false, mode: 'create' })
      const form = ref({
        language: '',
      })
      const formValid = ref(false)
      const formRef = ref(null)
      const tourStepsJson = ref('')
      const otherTranslationsJson = ref('')
      const jsonError = ref('')
      const otherJsonError = ref('')

      const theme = useTheme()
      const iconColor = computed(() =>
        theme.global.current.value.dark ? '#CECECE' : '#383935'
      )

      const isHelpDesk = computed(() => {
        const roles = store.getters['UserData/getRoles'] || []
        return roles.includes('helpDesk')
      })

      const headers = computed(() => {
        locale.value // touch to register dependency
        return [
          {
            title: t('uiTranslations.headers.language'),
            key: 'language',
            width: 110,
            align: 'center',
          },
          {
            title: t('uiTranslations.headers.tourSteps'),
            key: 'tourSteps',
            width: 150,
            align: 'center',
          },
          {
            title: t('uiTranslations.headers.updatedAt'),
            key: 'updatedAt',
            width: 150,
            align: 'center',
          },
          {
            title: t('uiTranslations.headers.actions'),
            key: 'actions',
            width: 120,
            align: 'end',
            sortable: false,
          },
        ]
      })

      const rules = {
        required: (v) => !!v || t('common.required'),
      }

      const formatDate = (timestamp) => {
        if (!timestamp) return '-'
        return new Date(timestamp.seconds * 1000).toLocaleDateString()
      }

      const validateJson = () => {
        try {
          if (tourStepsJson.value.trim()) {
            JSON.parse(tourStepsJson.value)
          }
          jsonError.value = ''
        } catch (e) {
          jsonError.value = t('uiTranslations.invalidJson')
        }
      }

      const validateOtherJson = () => {
        try {
          if (otherTranslationsJson.value.trim()) {
            JSON.parse(otherTranslationsJson.value)
          }
          otherJsonError.value = ''
        } catch (e) {
          otherJsonError.value = t('uiTranslations.invalidJson')
        }
      }

      const loadTranslations = async () => {
        loading.value = true
        try {
          const snapshot = await db.collection('uiTranslations').get()
          translations.value = snapshot.docs.map((doc) => {
            const data = doc.data()
            const tourSteps = data.tour?.steps || []
            return {
              id: doc.id,
              language: doc.id,
              tourSteps: Array.isArray(tourSteps) ? tourSteps.length : 0,
              updatedAt: doc.metadata?.updatedTime || null,
              data: data,
            }
          })
        } catch (error) {
          console.error('Error loading translations:', error)
        } finally {
          loading.value = false
        }
      }

      const openCreate = () => {
        dialog.value = { open: true, mode: 'create' }
        form.value = { language: '' }
        tourStepsJson.value = ''
        otherTranslationsJson.value = ''
        jsonError.value = ''
        otherJsonError.value = ''
      }

      const openEdit = (item) => {
        dialog.value = { open: true, mode: 'edit' }
        form.value = { language: item.language }

        // Load existing data
        const data = item.data
        tourStepsJson.value = data.tour?.steps
          ? JSON.stringify(data.tour.steps, null, 2)
          : ''

        // Load other translations (excluding tour)
        const otherData = { ...data }
        delete otherData.tour
        otherTranslationsJson.value =
          Object.keys(otherData).length > 0
            ? JSON.stringify(otherData, null, 2)
            : ''

        jsonError.value = ''
        otherJsonError.value = ''
      }

      const persist = async () => {
        if (!formValid.value || jsonError.value || otherJsonError.value) return

        try {
          const language = form.value.language
          const docRef = db.collection('uiTranslations').doc(language)

          // Parse tour steps
          let tourSteps = []
          if (tourStepsJson.value.trim()) {
            tourSteps = JSON.parse(tourStepsJson.value)
          }

          // Parse other translations
          let otherTranslations = {}
          if (otherTranslationsJson.value.trim()) {
            otherTranslations = JSON.parse(otherTranslationsJson.value)
          }

          // Combine data
          const data = {
            ...otherTranslations,
            tour: {
              steps: tourSteps,
            },
            updatedAt: new Date(),
          }

          await docRef.set(data, { merge: true })
          dialog.value.open = false
          await loadTranslations()
        } catch (error) {
          console.error('Error saving translation:', error)
        }
      }

      const remove = async (item) => {
        if (isHelpDesk.value) {
          const key = 'uiTranslations.deleteNotAllowed'
          const message = t(key)
          window.alert(
            message && message !== key
              ? message
              : 'Help Desk users cannot delete translations.'
          )
          return
        }

        if (confirm(t('uiTranslations.confirmDelete'))) {
          try {
            await db.collection('uiTranslations').doc(item.language).delete()
            await loadTranslations()
          } catch (error) {
            console.error('Error deleting translation:', error)
          }
        }
      }

      onMounted(() => {
        loadTranslations()
      })

      return {
        translations,
        loading,
        dialog,
        form,
        formValid,
        formRef,
        tourStepsJson,
        otherTranslationsJson,
        jsonError,
        otherJsonError,
        headers,
        iconColor,
        rules,
        formatDate,
        validateJson,
        validateOtherJson,
        openCreate,
        openEdit,
        persist,
        remove,
        isHelpDesk,
      }
    },
  }
</script>

<style scoped>
  .tj-cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tj-w-90 {
    width: 90px;
  }

  .tj-w-110 {
    width: 110px;
  }

  .tj-w-150 {
    width: 150px;
  }

  .tj-switch {
    transform: scale(0.8);
  }

  .tj-radio {
    transform: scale(0.8);
  }

  .tj-field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .tj-field-label {
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 4px;
    color: rgba(0, 0, 0, 0.6);
  }
</style>
